import './tableTools.css';
import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import IconTooltip from '../../common/icon_tooltip/iconTooltip';
import { useApiData } from '../../../contexts/apiService';
import ActiveCustomColIcon from '../../../assets/icons/active-custom-col.svg?react';
import type { LucideProps } from 'lucide-react';
import { Trash2, ListFilter, ListFilterPlus, Download, RefreshCcw, Columns3Cog }from 'lucide-react';
import { JsonColumnConfig, TableColumnConfig } from '../tableMain';
import { WarningTypes } from '../dataProcessing';
import { downloadTableAsExcel } from './excelDownload/excelDownload';
import WarningModal from './warningModal/warningModal';
import MonthNavigation from './monthNavigation/monthNavigation';
import SettingsModal from './settingsModal/settingsModal';
import { USER_ROLE_LEVEL } from '../../../contexts/userAccess';
import { getHoustonTime } from '../../../helper_functions';
import WarningBox from '../../common/WarningBox';
import { trackEvent } from '../../../contexts/userAnalytics';

const ActivePresetIcon = React.forwardRef<SVGSVGElement, LucideProps>(
    ({ size = 20, ...props }, ref) => (
        <ActiveCustomColIcon
            {...props}
            ref={ref}
            width={size}
            height={size}
        />
    )
);

ActivePresetIcon.displayName = 'ActivePresetIcon';

interface TableToolsProps {
    tableName: string;
    finalTableData?: Record<string, any>[]; // data prop to access the actual row data
    fullColumnConfigs: TableColumnConfig[];
    effectiveColumnConfigs: TableColumnConfig[];
    jsonColumnConfig?: JsonColumnConfig[];
    selectable: boolean;
    hasSelectedRows: boolean;
    selectedRowIndices: number[]; // selected row indices
    onClearSelection: () => void; // callback to clear row selection
    monthNavigation?: boolean; // if true, enable month navigation for table
    initialMonthYear?: string; // initial month to pre-seed MonthNavigation
    onMonthChange?: (monthYear: string) => void;
    deletable?: boolean; // if true, rows can be deleted
    deletionRequiresApproval?: boolean; // if true, deletions go through approval flow instead of direct delete
    downloadable?: boolean; // if true, table can be downloaded
    exportColumnOrder?: string[]; // column order for the Excel download (unlisted columns go after, in table order)
    refreshable?: boolean;
    enableSettings: boolean; // if true, enable table settings
    enableColumnConfiguration: boolean; // if true, enable column configuration settings
    isColumnConfigurationOpen: boolean;
    hasActiveColumnPreset: boolean;
    onVisibleColumnsChange?: (visible: string[] | null) => void;
    onOpenColumnConfiguration: () => void;
    enableWarnings?: boolean; // if true, enable table warnings
    warningData?: any; // precomputed warning data
    warningFilter?: any; // current warning filter applied
    onWarningFilter?: (key: string, description: string, type: WarningTypes) => void; // callback to apply warning filter (rule identifier, not bound functions)
    hasActiveFilters?: boolean; // prop to know if there are active filters
    onClearFilters?: () => void; // callback to clear all filters
    hasActiveSorts?: boolean; // prop to know if there are active sorts
    onClearSorts?: () => void; // callback to clear all sorts
    rowColor?: Array<{condition: (rowData: any) => boolean; color: string}>; // Adds a background color to the ro
    userData?: any; // User data for visibility checks
}

const TableTools: React.FC<TableToolsProps> = ({
    tableName,
    initialMonthYear,
    finalTableData = [],
    fullColumnConfigs,
    effectiveColumnConfigs,
    jsonColumnConfig,
    selectable,
    hasSelectedRows,
    selectedRowIndices = [],
    onClearSelection,
    monthNavigation,
    onMonthChange,
    deletable,
    deletionRequiresApproval,
    downloadable,
    exportColumnOrder,
    refreshable,
    enableSettings,
    enableColumnConfiguration,
    isColumnConfigurationOpen,
    hasActiveColumnPreset,
    onOpenColumnConfiguration,
    enableWarnings = false,
    warningData,
    warningFilter,
    onWarningFilter,
    hasActiveFilters = false,
    onClearFilters,
    hasActiveSorts = false,
    onClearSorts,
    rowColor,
    userData,
}) => {

    const { handleDeleteRow, handleGetTable, handleAddRow, userDetails } = useApiData();
    // Matches the id-column visibility gate in tableMain.tsx — only admins can
    // browse/filter actively-suppressed warnings for reconciliation.
    const isAdminUser = userDetails?.role === 'admin' || userDetails?.role === 'super_admin';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [currentMonthFilter, setCurrentMonthFilter] = useState<string>('');
    
    const handleRowDeletion = () => {
        if (deletionRequiresApproval) {
            const userRoleLevel = userDetails ? USER_ROLE_LEVEL[userDetails.role as keyof typeof USER_ROLE_LEVEL] ?? 1 : 1;
            if (userDetails && userRoleLevel >= USER_ROLE_LEVEL['scheduler']) {
                setShowDeleteConfirm(true);
            } else {
                // viewers and below cannot request deletions — show explicit feedback instead of doing nothing
                window.alert('You do not have permission to request deletions. Please contact a scheduler or administrator if you believe this is an error.');
            }
        } else {
            setShowDeleteConfirm(true);
        }
    };

    const handleConfirmDeletion = async (reason?: string) => {
        setShowDeleteConfirm(false);
        if (onClearSelection) {
            onClearSelection();
        }
        try {
            setIsSubmitting(true);

            if (deletionRequiresApproval) {
                for (const rowId of selectedRowIndices) {
                    const rowData = finalTableData.find(row => row.id == rowId) || {};
                    await handleAddRow('pending_approvals', {
                        table_name: tableName,
                        action: 'delete',
                        request_user: userDetails!.name,
                        request_user_role: userDetails!.role,
                        request_user_id: userDetails!.id,
                        edited_col: null,
                        new_value: null,
                        requested_on: getHoustonTime(),
                        old_value: null,
                        request_reason: reason || '',
                        status: 'Pending',
                        pending_row_id: rowId,
                        pdf_ticket_number: rowData.pdf_ticket_number || rowData.ticket_number || null,
                        rid: rowData.rid || null,
                    });
                }
            } else {
                await handleDeleteRow(new Set(selectedRowIndices), tableName);
            }
        } catch (error) {
            console.error('Error deleting row:', error);
        } finally {
            setIsSubmitting(false);
            if (onClearSelection) {
                onClearSelection();
            }
        }
    };

    // Handle clearing both filters and sorts
    const handleClearFiltersAndSorts = () => {
        if (onClearFilters) {
            onClearFilters();
        }
        if (onClearSorts) {
            onClearSorts();
        }
        if ((window as any).resetWarningSelection) {
            (window as any).resetWarningSelection();
        }
    };
    
    const handleDownload = () => {
        const rank = (colName: string) => {
            const i = exportColumnOrder?.indexOf(colName) ?? -1;
            return i === -1 ? Infinity : i;
        };
        const exportColumns = exportColumnOrder
            ? [...effectiveColumnConfigs].sort((a, b) => rank(a.colName) - rank(b.colName))
            : effectiveColumnConfigs;
        downloadTableAsExcel({
            data: finalTableData || [],
            effectiveColumnConfigs: exportColumns,
            jsonColumnConfig,
            tableName,
            rowColor,
            userData,
        });
        trackEvent('excel_download', {
            tableName: tableName,
        });
    };

    const handleMonthChange = useCallback((monthYear: string) => {
        setCurrentMonthFilter(monthYear);

        // Pass it to parent
        if (onMonthChange) {
            onMonthChange(monthYear);
        }
    }, [onMonthChange]);

    const handleTableRefresh = async () => {
        try {
            setIsRefreshing(true);
            if (monthNavigation) {
                await handleGetTable(tableName, currentMonthFilter, false);
            } else {
                await handleGetTable(tableName);
            }
        } catch (error) {
            console.error('Error refreshing table:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const isDeleteDisabled = !hasSelectedRows || isSubmitting;
    const isClearFiltersDisabled = !hasActiveFilters && !hasActiveSorts && (warningFilter && Object.keys(warningFilter).length === 0);
    const hasActiveState = hasActiveFilters || hasActiveSorts || (warningFilter && Object.keys(warningFilter).length > 0);

    return (
        <div 
            className="tableTools-mainContainer"
        >
            <div className="tableTools-buttonsGroup">
                {enableSettings && (
                    <SettingsModal
                        finalTableData={finalTableData}
                        hasActiveFilters={hasActiveFilters}
                        currentMonthFilter={currentMonthFilter}
                        tableName={tableName}
                    />
                )}
                <IconTooltip 
                    icon={hasActiveState ? ListFilterPlus : ListFilter}
                    disabled={isClearFiltersDisabled}
                    onClick={handleClearFiltersAndSorts}
                    showTooltip={true}
                    label="Clear Filters"
                    position="bottom"
                    iconColor={hasActiveState ? 'var(--color-blue-default)' : 'var(--color-grey-dark)'}
                />
                {selectable && deletable &&(
                    <IconTooltip
                        icon={Trash2}
                        disabled={isDeleteDisabled}
                        onClick={handleRowDeletion}
                        showTooltip={true}
                        label="Delete"
                        position="bottom"
                        iconColor='var(--color-red-delete)'
                    />
                )}
                {downloadable && (
                    <IconTooltip 
                        icon={Download}
                        onClick={handleDownload}
                        showTooltip={true}
                        label="Download Table"
                        position="bottom"
                        iconColor='var(--color-green-download)'
                    />
                )}
                {refreshable && (
                    <IconTooltip 
                        icon={RefreshCcw}
                        onClick={handleTableRefresh}
                        disabled={isRefreshing}
                        isLoading={isRefreshing}
                        showTooltip={true}
                        label="Refresh Table"
                        position="bottom"
                        iconColor='var(--color-blue-default)'
                    />
                )}
                {enableColumnConfiguration &&(
                    <IconTooltip
                        icon={hasActiveColumnPreset ? ActivePresetIcon : Columns3Cog}
                        onClick={onOpenColumnConfiguration}
                        showTooltip={true}
                        label="Column Configuration"
                        position="bottom"
                        iconColor={
                            hasActiveColumnPreset ? 'var(--color-red-delete-dark)' :
                            isColumnConfigurationOpen ? 'var(--color-blue-default)' :
                            'var(--color-grey-light-text)'
                        }
                        isActive={isColumnConfigurationOpen}
                    />
                )}
            </div>
            
            {monthNavigation && (
                <div className="tableTools-navigationGroup">
                    <MonthNavigation
                        tableName={tableName}
                        initialMonth={initialMonthYear}
                        onMonthChange={handleMonthChange}
                    />
                </div>
            )}

            <div className="tableTools-warningsGroup">
                <WarningModal
                    enableWarnings={enableWarnings}
                    onWarningFilter={onWarningFilter}
                    warningData={warningData}
                    onClearFiltersAndSorts={handleClearFiltersAndSorts}
                    onResetWarning={() => {}}
                    canViewSuppressed={isAdminUser}
                />
            </div>
                
            {showDeleteConfirm && createPortal(
                <WarningBox
                    isOpen={showDeleteConfirm}
                    onClose={() => setShowDeleteConfirm(false)}
                    onConfirm={handleConfirmDeletion}
                    title="Delete Request"
                    message={deletionRequiresApproval
                        ? 'The request will be moved to approvals. Please indicate the reason for deletion.'
                        : 'Are you sure you want to delete the selected row(s)?'
                    }
                    button1="Cancel"
                    button2="Submit Request"
                    showbutton2={true}
                    showTextInput={deletionRequiresApproval}
                    textPlaceholder='Type reason for deletion...'
                    button2Color='green'
                />,
                document.body
            )}
        </div>
    );
};

export default TableTools;